import { Button } from '@shared/components/ui/button';
import { DatePicker } from '@shared/components/ui/date-picker';
import { Input } from '@shared/components/ui/input';
import { ModalCenter, ModalCenterRef } from '@shared/components/ui/ModalCenter';
import { useToast } from '@shared/hooks/use-toast';
import { format } from 'date-fns';
import { forwardRef, useCallback, useImperativeHandle, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useBlockBatch } from '../../../hooks/useBatchMutations';
import type { Batch } from '../../../models/Batch';

interface ExtendedModalCenterRef extends ModalCenterRef {
  openWithBatch: (batch: Batch) => void;
}

const BlockBatchModal = forwardRef<ExtendedModalCenterRef, object>((_, ref) => {
  const { t } = useTranslation('batchesModule');
  const { toast } = useToast();
  const blockBatch = useBlockBatch();
  const internalModalRef = useRef<ModalCenterRef>(null);

  const [batch, setBatch] = useState<Batch | null>(null);
  const [reason, setReason] = useState('');
  const [blockedUntilDate, setBlockedUntilDate] = useState<Date | undefined>(undefined);
  const [allowEntries, setAllowEntries] = useState(false);

  const resetForm = () => {
    setBatch(null);
    setReason('');
    setBlockedUntilDate(undefined);
    setAllowEntries(false);
  };

  const handleCancel = () => {
    internalModalRef.current?.closeModal();
    resetForm();
  };

  const handleSubmit = async () => {
    if (!batch) return;

    try {
      await blockBatch.mutateAsync({
        batchId: batch.id,
        data: {
          reason: reason.trim() || null,
          blocked_until: blockedUntilDate ? format(blockedUntilDate, 'yyyy-MM-dd') : null,
          allow_entries: allowEntries,
        },
      });

      toast({ title: t('toast.blockSuccess'), variant: 'default' });
      handleCancel();
    } catch (error: any) {
      toast({
        title: t('toast.blockError'),
        description: error?.response?.data?.message || error.message,
        variant: 'destructive',
      });
    }
  };

  const openWithBatch = useCallback((batchData: Batch) => {
    setBatch(batchData);
    internalModalRef.current?.openModal();
  }, []);

  useImperativeHandle(
    ref,
    () => ({
      openModal: () => internalModalRef.current?.openModal(),
      closeModal: () => internalModalRef.current?.closeModal(),
      openWithBatch,
    }),
    [openWithBatch],
  );

  return (
    <ModalCenter ref={internalModalRef} title={t('modals.block.title')} type="warning">
      <div className="space-y-4">
        <div className="space-y-2">
          <label className="text-sm font-medium text-text">{t('modals.block.reason')}</label>
          <Input
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder={t('modals.block.reason_placeholder')}
          />
        </div>

        <div className="space-y-2">
          <label className="text-sm font-medium text-text">{t('modals.block.blocked_until')}</label>
          <DatePicker
            date={blockedUntilDate}
            onDateChange={setBlockedUntilDate}
            placeholder={t('modals.create.date_placeholder')}
          />
        </div>

        <label className="flex items-center gap-2 cursor-pointer">
          <input
            type="checkbox"
            checked={allowEntries}
            onChange={(e) => setAllowEntries(e.target.checked)}
            className="rounded border-card-border"
          />
          <span className="text-sm text-text">{t('modals.block.allow_entries')}</span>
        </label>

        <div className="flex justify-end gap-2 pt-2">
          <Button variant="outline" size="lg" onClick={handleCancel} disabled={blockBatch.isPending}>
            {t('modals.block.cancel')}
          </Button>
          <Button
            variant="primary"
            size="lg"
            className="text-white"
            onClick={handleSubmit}
            disabled={blockBatch.isPending}
          >
            {t('modals.block.submit')}
          </Button>
        </div>
      </div>
    </ModalCenter>
  );
});

BlockBatchModal.displayName = 'BlockBatchModal';

export default BlockBatchModal;
